import { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { GridColDef } from '@mui/x-data-grid';

import SearchField from '../../common/searchField';
import DataTable from '../../components/DataTable';

import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../utils/table-utils';
import { connectivityDashboardActions } from '../../store/reducers/connectivity-dashboard';
import { GatewaysLatestRequestPayloadTypes } from '../../store/models/connectivity-dashboard';

import { ReactComponent as GatewayConnectedIcon } from '../../assets/connectivitydashboard/gateway-connected-icon.svg';
import { ReactComponent as GatewayDisconnectedIcon } from '../../assets/connectivitydashboard/gateway-disconnected-icon.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/main/filter-icon.svg';

import { useLocation, useOutletContext, useParams, useNavigate } from 'react-router-dom';
import { pageActions } from '../../store/reducers/page-actions';
import { palette } from '../../theme/palette';

import LatestMessagePopup from './latestMessagePopup';
import { ontologyMasterActions } from '../../store/reducers/ontology-master';

import { convertUTCtoLocal } from '../../utils/time-converter';
import Stack from '@mui/material/Stack';
import { lighten, useTheme } from '@mui/material';
import get from 'lodash/get';
import IconButton from '@mui/material/IconButton';
import OntologySelector from '../../common/OntologySelector';

const Styles = {
  container: { boxShadow: '0px 0px 14px 0px rgba(0, 0, 0, 0.02)', borderRadius: '18px' },
  paper: {
    margin: 'auto',
    boxShadow: '0px 0px 14px 0px #0000000D',
    borderRadius: '18px',
    border: `1px solid #D9DFED`,
    // height: '80vh',
    overflow: 'hidden',
  },
  header: {
    p: 3,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    boxSizing: 'border-box',
    flexWrap: 'wrap',
    rowGap: 1,
    // height: '87px',
  },
  searchFieldWrapper: { display: 'flex', alignItems: 'center', columnGap: 3 },
  dataTableWrapper: {
    height: '100%',
    overflow: 'hidden',
  },
  dataTable: {
    '& .MuiDataGrid-virtualScroller': {
      overflow: 'auto',
      overflowY: 'hidden',
      minHeight: 'calc(100vh - 420px)',
      // paddingBottom: '10px',
      height: '100%',
    },
    '&.MuiDataGrid-root': {
      // height:'100%',
      minHeight: 'calc(100vh - 278px)',
    },
    '.MuiDataGrid-columnHeader': {
      height: '60px !important',
    },
    '.MuiDataGrid-columnHeaders': {
      minHeight: '60px !important',
      maxHeight: '60px !important',
      lineHeight: '60px !important',
    },
  },
  closeBtnWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBlock: 4,
    borderTop: '1px solid #D9DFED',
  },
  tableCellText: {
    fontSize: '16px',
    fontWeight: 400,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  connectedIcon: { paddingLeft: '8px' },
  disConnectedIcon: { cursor: 'pointer', paddingLeft: '8px' },
  disabledIcon: { color: '#A8B1C7' },
};

interface GatewaysLabelTypes {
  all: string;
  connected: string;
  disconnected: string;
}

const gatewaysLabel: GatewaysLabelTypes = {
  all: '',
  connected: 'CONNECTED',
  disconnected: 'DISCONNECTED',
};

const GatewaysTable = ({
  gateways,
  gatewaysLoading,
  gatewaysPageNumber,
  gatewaysTotalCount,
  ontologyData,
  ontologies,
}: any) => {
  const theme = useTheme();
  const themeMode: any = get(useOutletContext(), 'themeMode', {});
  const dispatch = useDispatch();
  const [sortingModel, setSortingModel] = useState({ field: '', sort: '' });
  const [searchValue, setSearchValue] = useState('');
  const { onto_manage_id, type } = useParams();
  const { pathname } = useLocation();
  const [gatewaysList, setGatewaysList] = useState<any>([]);
  const [popupMsgData, setPopupMsgData] = useState<any>({
    status: false,
    title: '',
    type: '',
    item: {},
    connectivityStatus: '',
  });
  // const [loading, setLoading] = useState(true);
  const [gatewaysFilterBy, setGatewaysFilterBy] = useState('');
  const navigate = useNavigate();

  const dispatchGatewaysRequest = (payload: GatewaysLatestRequestPayloadTypes) => {
    dispatch(
      connectivityDashboardActions.getGatewaysRequest({
        search_item: searchValue,
        connectivity_status: type === 'viewall' ? '' : type,
        onto_manage_id: Number(onto_manage_id),
        filter_by: gatewaysFilterBy,
        timestamp: new Date().getTime(),
        ...sortingModel,
        ...payload,
      })
    );
  };

  useEffect(() => {
    if (type) {
      // setLoading(true);
      dispatchGatewaysRequest({ limit: DEFAULT_PAGE_SIZE, page: DEFAULT_PAGE_NUMBER });
      // dispatch(connectivityDashboardActions.getGatewaysRequest({ limit: 100, page: 1 }));
      dispatch(ontologyMasterActions.getOntologyMgmntList(''));
      dispatch(ontologyMasterActions.getOntologyList(''));
    }

    return () => {
      dispatch(connectivityDashboardActions.resetGatewaysTable());
    };
  }, [type]);

  useEffect(() => {
    if (gateways && gateways.length > 0 && ontologies.length) {
      setGatewaysList(
        gateways.map((item: any) => {
          return {
            ...item,
            ontologyList: getOntologyData(item.onto_manage_id),
          };
        })
      );
    } else {
      setGatewaysList([]);
    }
  }, [gateways, ontologies]);

  const onSearch = (value: string) => {
    // setLoading(true);
    dispatchGatewaysRequest({ limit: DEFAULT_PAGE_SIZE, page: DEFAULT_PAGE_NUMBER, search_item: value });
    setSearchValue(value);
  };

  useEffect(() => {
    if (pathname.length) {
      const pathnamesTemp: any = pathname.split('/').filter(Boolean);
      if (pathnamesTemp.length) {
        const pathnamesData: any = pathnamesTemp
          .filter((i: any, index: number) => index != 2 && index != 3)
          .map((item: any, index: number) => {
            if (index == 0) {
              return { route: pathnamesTemp[0], pathName: item.replace('-', ' '), navigate: true };
            } else if (index == 1) {
              return { route: '', pathName: 'Gateways', navigate: false };
            } else {
              return { route: '', pathName: type, navigate: false };
            }
          });
        dispatch(pageActions.setPagePath(pathnamesData));
        return () => {
          const data: any = [];
          dispatch(pageActions.setPagePath(data));
        };
      }
    }
  }, [pathname]);

  useEffect(() => {
    let gatewaysInterval: any;
    if (!gatewaysLoading) {
      gatewaysInterval = setInterval(() => {
        dispatch(
          connectivityDashboardActions.getGatewaysPollingRequest({
            connectivity_status: type === 'viewall' ? '' : type,
            onto_manage_id: Number(onto_manage_id),
            limit: DEFAULT_PAGE_SIZE,
            page: gatewaysPageNumber,
            search_item: searchValue,
            filter_by: gatewaysFilterBy,
            timestamp: new Date().getTime(),
            ...sortingModel,
          })
        );
      }, 60000);
    }

    return () => {
      clearInterval(gatewaysInterval);
    };
  }, [sortingModel, searchValue, gatewaysPageNumber, gatewaysLoading, gatewaysFilterBy]);

  const getOntologyData = (ontoId: any) => {
    if (ontologies.length > 0) {
      let tempOntoData: any = [];
      restartTheLoop: while (true) {
        for (const item of ontologies) {
          if (item.onto_manage_id == ontoId) {
            tempOntoData.unshift(item);
            ontoId = item.parent_id;
            continue restartTheLoop;
          }
        }
        break;
      }

      tempOntoData.map((item: any) => {
        let getLevelName = ontologyData.filter((data: any) => {
          return data.id === item.onto_level_number;
        })[0];

        return {
          ...item,
          level_name: getLevelName?.level_name || '',
        };
      });

      return tempOntoData;
    }
    return [];
  };

  const connectedIconStyle = { ...Styles.connectedIcon, paddingLeft: 0, color: theme.palette.success.main };
  const disconnectedIconStyle = { ...Styles.disConnectedIcon, paddingLeft: 0, color: theme.palette.error.main };

  const columnsStatuses: GridColDef[] = [
    {
      field: 'gateway_name',
      headerName: 'Gateway Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography component='p' sx={Styles.tableCellText}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      minWidth: 300,
      renderCell: (params: any) => {
        const ontoList =
          params.row.ontologyList && params.row.ontologyList.length
            ? params.row.ontologyList.map((item: any, index: number) =>
              params.row.ontologyList.length > 1 ? (
                index === params.row.ontologyList.length - 1 ? (
                  <span key={item.onto_manage_title}>{item.onto_manage_title} </span>
                ) : (
                  <span key={item.onto_manage_title}>{item.onto_manage_title} / </span>
                )
              ) : (
                <span key={item.onto_manage_title}>{item.onto_manage_title}</span>
              )
            )
            : '';

        return (
          <Box sx={{ textWrap: 'wrap', lineHeight: '1.2', width: '280px' }}>
            <Tooltip title={ontoList} placement='left' arrow>
              <Typography
                component='p'
                sx={{
                  textOverflow: 'ellipsis',
                  fontSize: '16px !important',
                  fontWeight: '400',
                  lineHeight: '1.5',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {ontoList}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'assets_count',
      headerName: 'Assets Connected',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          component='p'
          sx={{
            fontSize: '16px',
            fontWeight: 400,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'last_connected',
      headerName: 'Last Message Time',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const lastConnected = convertUTCtoLocal(params.value);
        return (
          <Tooltip title={lastConnected} arrow>
            <Typography component='p' sx={Styles.tableCellText}>
              {lastConnected}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'connectivity_status',
      headerName: 'Status',
      width: 200,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (params.row.connectivity_status === 'connected') {
          return <GatewayConnectedIcon style={connectedIconStyle} />
        } else if (params.row.connectivity_status === 'disconnected') {
          return (
            <GatewayDisconnectedIcon
              style={disconnectedIconStyle}
              onClick={() => handleMessagePopupOpen('Gateway Connection', 'Disconnected', params?.row)}
            />
          );
        } else {
          return <GatewayConnectedIcon style={Styles.disabledIcon} />;
        }
      },
      minWidth: 100,
    },
  ];

  const columnsAll: GridColDef[] = [
    {
      field: 'gateway_name',
      headerName: 'Gateway Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <Typography component='p' sx={Styles.tableCellText}>
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'location',
      headerName: 'Location',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      minWidth: 300,
      renderCell: (params: any) => {
        const ontoList =
          params.row.ontologyList && params.row.ontologyList.length
            ? params.row.ontologyList.map((item: any, index: number) =>
              params.row.ontologyList.length > 1 ? (
                index === params.row.ontologyList.length - 1 ? (
                  <span key={item.onto_manage_title}>{item.onto_manage_title} </span>
                ) : (
                  <span key={item.onto_manage_title}>{item.onto_manage_title} / </span>
                )
              ) : (
                <span key={item.onto_manage_title}>{item.onto_manage_title}</span>
              )
            )
            : '';

        return (
          <Box sx={{ textWrap: 'wrap', lineHeight: '1.2', width: '280px' }}>
            <Tooltip title={ontoList} placement='left' arrow>
              <Typography
                component='p'
                sx={{
                  textOverflow: 'ellipsis',
                  fontSize: '16px !important',
                  fontWeight: '400',
                  lineHeight: '1.5',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {ontoList}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'assets_count',
      headerName: 'Assets Connected',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          component='p'
          sx={{
            fontSize: '16px',
            fontWeight: 400,
          }}
        >
          {params.value}
        </Typography>
      ),
    },

    {
      field: 'last_connected',
      headerName: 'Last Message Time',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const lastConnected = convertUTCtoLocal(params.value);
        return (
          <Tooltip title={lastConnected} arrow>
            <Typography component='p' sx={Styles.tableCellText}>
              {lastConnected}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'connectivity_status',
      headerName: 'Status',
      width: 200,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        if (params.row.connectivity_status === 'connected') {
          return <GatewayConnectedIcon style={connectedIconStyle} />;
        } else if (params.row.connectivity_status === 'disconnected') {
          return (
            <GatewayDisconnectedIcon
              style={disconnectedIconStyle}
              onClick={() => handleMessagePopupOpen('Gateway Connection', 'Disconnected', params?.row)}
            />
          );
        } else {
          return <GatewayConnectedIcon style={Styles.disabledIcon} />;
        }
      },
      minWidth: 100,
    },
  ];

  const handleMessagePopupOpen = (title: string, type: any, item: any) => {
    setPopupMsgData({ status: true, title: title, type: type, item: item?.latest_message || item?.payload || {} });
  };
  const handleMsgPopupClose = () => {
    setPopupMsgData({ status: false, title: '', type: '', item: {}, connectivityStatus: '' });
  };

  const onGatewaysFilterBy = (key: string) => {
    const filterByStatus = gatewaysFilterBy ? '' : key;
    dispatchGatewaysRequest({ limit: DEFAULT_PAGE_SIZE, page: DEFAULT_PAGE_NUMBER, filter_by: filterByStatus });
    setGatewaysFilterBy(filterByStatus);
  };

  return (
    <Container maxWidth={false} disableGutters sx={Styles.container}>
      <Box sx={{ mb: 4 }}>
        <OntologySelector
          padding='0'
          ontologyData={ontologyData}
          showLayout={true}
          hideBottomRow={true}
          onOntoFilterChange={({ onto_manage_id }: { onto_manage_id: number }) => {
            dispatchGatewaysRequest({
              onto_manage_id: Number(onto_manage_id),
            });
            navigate(`/connectivity-dashboard/gateways/${onto_manage_id}/${type}`, {
              replace: true,
            });
          }}
          hideLine={true}
          ontoManageId={Number(onto_manage_id)}
        />
      </Box>
      <Paper
        sx={{
          ...Styles.paper,
          border: `1px solid  ${theme.palette.customBorderColor.main}`,
          backgroundColor: themeMode.isLightBG
            ? lighten(theme.palette.background.default, 0.9)
            : lighten(theme.palette.background.default, 0.05),
        }}>
        <Box sx={Styles.header}>
          <Box>
            <Typography component='h4' fontSize='18px'>
              List of <span style={{ textTransform: 'capitalize' }}>{type === 'viewall' ? '' : type}</span> Gateways
            </Typography>
          </Box>
          <Box sx={Styles.searchFieldWrapper}>
            {type === 'viewall' && (
              <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 2 }}>
                <Box sx={{ display: 'flex', columnGap: '12px' }}>
                  <Box sx={{ display: 'flex', columnGap: 1 }}>
                    <FilterIcon stroke='#51648f' />
                    <Typography sx={{ fontSize: '16px', color: palette.disabled.dark }}>Filter By :</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
                    <IconButton sx={{ p: 0 }} onClick={() => onGatewaysFilterBy('connectivity_status')}>
                      <GatewayDisconnectedIcon
                        style={{
                          color: gatewaysFilterBy ? palette.error.main : '#A8B1C7',
                        }}
                      />
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ height: '42px', width: '1px', bgcolor: palette.secondary.main }} />
              </Box>
            )}
            <SearchField searchPlaceholder='Search' onSearch={onSearch} width='100%' />
          </Box>
        </Box>
        <Box sx={Styles.dataTableWrapper}>
          <DataTable
            columns={type === 'viewall' ? columnsAll : columnsStatuses}
            rows={gatewaysList}
            tableStyles={Styles.dataTable}
            loading={gatewaysLoading}
            noRowsOverlay={() => (
              <Stack height='100%' alignItems='center' justifyContent='center'>
                No Gateways
              </Stack>
            )}
            isServerPagination
            totalRowCount={gatewaysTotalCount}
            pageNumber={gatewaysPageNumber}
            onPagination={(pageSize: number, pageNumber: number) => {
              dispatchGatewaysRequest({
                limit: pageSize,
                page: pageNumber,
              });
            }}
            onSorting={(sortModel: { field: string; sort: string }) => {
              dispatchGatewaysRequest({
                limit: DEFAULT_PAGE_SIZE,
                page: gatewaysPageNumber,
                ...sortModel,
              });
              setSortingModel(sortModel);
            }}
            otherProps={{
              getRowId: (row: any) => row.gateway_id,
            }}
          />
        </Box>
      </Paper>
      {popupMsgData.status && (
        <LatestMessagePopup popupMsgData={popupMsgData} handleMsgPopupClose={handleMsgPopupClose} />
      )}
    </Container>
  );
};

const mapStateToProps = (state: any) => ({
  gateways: state.connectivityDashboard.gateways,
  gatewaysLoading: state.connectivityDashboard.gatewaysLoading,
  gatewaysPageNumber: state.connectivityDashboard.gatewaysPageNumber,
  gatewaysTotalCount: state.connectivityDashboard.gatewaysTotalCount,
  ontologyData: state.ontologyMaster.ontologyData,
  ontologies: state.ontologyMaster.ontologyMgmntData,
});

export default connect(mapStateToProps)(GatewaysTable);
